/**
 * MCP-Enabled AI Chat Component
 *
 * Enhanced AI Chat with tool execution capabilities for model-aware conversations.
 * The AI can read, query, modify, and validate the IDF model through MCP tools.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Markdown, { type Components } from 'react-markdown';
import { cn } from '../../lib/utils';
import { useAIStore, selectHasApiKey } from '../aiStore';
import { useIDFStore } from '../../stores/idfStore';
import { useIDDStore } from '../../stores/iddStore';
import { useIsMobile } from '../../hooks';
import { useWizardState } from '../../visual-object-editor/visualObjectEditorStore';
import { useMCPTools } from '../../mcp/useMCPTools';
import { useOnboardingStore } from '../../onboarding';
import { MCP_SYSTEM_PROMPT, MCP_TOOLS } from '../../mcp/mcpToolDefinitions';
import {
  RateLimitError,
  formatRateLimitErrorForUser,
} from '../rateLimitHandler';
import type { ChatMessage } from '../types';
import type { MCPToolCall, MCPToolResult } from '../../mcp/types';

/**
 * Content block type for Claude API messages
 */
interface ContentBlock {
  type: 'text' | 'tool_use' | 'tool_result';
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  tool_use_id?: string;
  content?: string;
  is_error?: boolean;
}

/**
 * Sanitize messages to ensure tool_use blocks have corresponding tool_results.
 * The Claude API requires that every tool_use must be immediately followed by a tool_result.
 * If we find orphaned tool_use blocks (assistant message with tool_use that isn't followed
 * by a tool_result user message), we strip the tool_use blocks from the assistant message.
 */
function sanitizeMessagesForToolUse(
  messages: Array<{ role: string; content: unknown }>
): Array<{ role: string; content: unknown }> {
  const sanitized: Array<{ role: string; content: unknown }> = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg) continue;

    // Check if this is an assistant message with tool_use blocks
    if (msg.role === 'assistant' && Array.isArray(msg.content)) {
      const contentBlocks = msg.content as ContentBlock[];
      const toolUseBlocks = contentBlocks.filter((b) => b.type === 'tool_use');

      if (toolUseBlocks.length > 0) {
        // Check if the next message is a user message with tool_result blocks
        const nextMsg = messages[i + 1];
        const hasToolResults = nextMsg &&
          nextMsg.role === 'user' &&
          Array.isArray(nextMsg.content) &&
          (nextMsg.content as ContentBlock[]).some((b) => b.type === 'tool_result');

        if (!hasToolResults) {
          // Orphaned tool_use - strip tool_use blocks, keep only text
          const textBlocks = contentBlocks.filter((b) => b.type === 'text');
          const textContent = textBlocks.map(b => b.text || '').join('\n').trim();

          if (textContent) {
            sanitized.push({
              role: msg.role,
              content: textContent,
            });
          }
          // Skip this message if there's no text content
          continue;
        }
      }
    }

    sanitized.push(msg);
  }

  return sanitized;
}

/**
 * Tool execution status display
 */
interface ToolExecution {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  result?: MCPToolResult;
}

/**
 * Format timestamp to time string
 */
function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Book icon for docs link button
 */
function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

/**
 * Cloud icon for weather action button
 */
function CloudIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    </svg>
  );
}

/**
 * Play icon for simulation action button
 */
function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

/**
 * Arrow right icon for navigation action button
 */
function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

/**
 * Get icon component for action type
 */
function getActionIcon(actionType: string): React.ComponentType<{ className?: string }> {
  switch (actionType) {
    case 'select-weather':
    case 'go-to-weather':
      return CloudIcon;
    case 'go-to-simulation':
    case 'run-simulation':
      return PlayIcon;
    default:
      return ArrowRightIcon;
  }
}

/**
 * Get button style for action type
 */
function getActionStyle(actionType: string): string {
  switch (actionType) {
    case 'select-weather':
    case 'go-to-weather':
      return 'bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border-cyan-600/30';
    case 'go-to-simulation':
    case 'run-simulation':
      return 'bg-green-600/20 hover:bg-green-600/30 text-green-300 border-green-600/30';
    default:
      return 'bg-primary/20 hover:bg-primary/30 text-primary-foreground border-primary/30';
  }
}

/**
 * Create markdown components with action handler
 */
function createMarkdownComponents(onAction: (action: string) => void): Components {
  return {
    a: ({ href, children }) => {
      // Check if this is an action link (/action/*)
      if (href?.startsWith('/action/')) {
        const actionType = href.slice(8); // Remove '/action/' prefix
        const IconComponent = getActionIcon(actionType);
        const buttonStyle = getActionStyle(actionType);

        return (
          <button
            onClick={() => { onAction(actionType); }}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 my-1 rounded-md border text-sm font-medium transition-colors',
              buttonStyle
            )}
          >
            <IconComponent className="w-4 h-4" />
            {children}
          </button>
        );
      }

      // Check if this is a docs link (internal /docs path)
      if (href?.startsWith('/docs')) {
        return (
          <Link
            to={href}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 rounded-md bg-primary/20 hover:bg-primary/30 text-primary-foreground border border-primary/30 text-sm font-medium transition-colors no-underline"
          >
            <BookOpenIcon className="w-4 h-4" />
            {children}
          </Link>
        );
      }
      // Regular external link
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
          {children}
        </a>
      );
    },
  };
}

/**
 * Tool execution indicator
 */
function ToolExecutionIndicator({ executions }: { executions: ToolExecution[] }) {
  if (executions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 my-2">
      {executions.map((exec) => (
        <div
          key={exec.id}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded text-xs',
            exec.status === 'running' && 'bg-blue-900/50 text-blue-300',
            exec.status === 'completed' && 'bg-green-900/50 text-green-300',
            exec.status === 'error' && 'bg-red-900/50 text-red-300',
            exec.status === 'pending' && 'bg-gray-700 text-gray-400'
          )}
        >
          {exec.status === 'running' && (
            <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          )}
          {exec.status === 'completed' && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {exec.status === 'error' && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="font-mono">{exec.name}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Creative thinking indicator with animated dots
 * Shows when the AI is processing but hasn't started responding yet
 */
function ThinkingIndicator() {
  return (
    <div className="flex gap-3 justify-start">
      {/* AI Avatar */}
      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 animate-thinking-glow">
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>

      {/* Thinking Bubble */}
      <div className="bg-gray-700 rounded-lg px-3 py-2 flex items-center gap-2">
        {/* Animated dots with wave effect */}
        <div className="flex items-center gap-1">
          <span
            className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-thinking-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <span
            className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-thinking-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <span
            className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-thinking-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>

        {/* Thinking text with subtle animation */}
        <span className="text-xs text-gray-400 animate-pulse">
          Thinking
        </span>
      </div>
    </div>
  );
}

/**
 * Individual chat message component
 */
function ChatMessageItem({
  message,
  toolExecutions,
  markdownComponents,
}: {
  message: ChatMessage;
  toolExecutions?: ToolExecution[];
  markdownComponents: Components;
}) {
  const isUser = message.role === 'user';

  return (
    <div className="space-y-1">
      <div
        className={cn(
          'flex gap-3',
          isUser ? 'justify-end' : 'justify-start'
        )}
      >
        {/* Avatar */}
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        )}

        {/* Message Bubble */}
        <div
          className={cn(
            'max-w-[85%] rounded-lg px-4 py-2',
            isUser
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-200'
          )}
        >
          {isUser ? (
            <div className="text-sm whitespace-pre-wrap break-words">
              {message.content}
            </div>
          ) : (
            <div className="text-sm prose prose-sm prose-invert max-w-none prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-pre:my-2 prose-code:bg-gray-800 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-800 prose-pre:p-2">
              <Markdown components={markdownComponents}>{message.content}</Markdown>
              {message.isStreaming && (
                <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />
              )}
            </div>
          )}
          <div
            className={cn(
              'text-xs mt-1',
              isUser ? 'text-blue-200' : 'text-gray-400'
            )}
          >
            {formatTime(message.timestamp)}
          </div>
        </div>

        {/* User Avatar */}
        {isUser && (
          <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}
      </div>

      {/* Tool executions for assistant messages */}
      {!isUser && toolExecutions && toolExecutions.length > 0 && (
        <div className="ml-11">
          <ToolExecutionIndicator executions={toolExecutions} />
        </div>
      )}
    </div>
  );
}

/**
 * Chat input component
 */
function ChatInput({
  onSend,
  disabled,
}: {
  onSend: (message: string) => void;
  disabled: boolean;
}) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed && !disabled) {
        onSend(trimmed);
        setInput('');
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    },
    [input, disabled, onSend]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  const handleInput = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${String(Math.min(textareaRef.current.scrollHeight, 120))}px`;
    }
  }, []);

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <textarea
        ref={textareaRef}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          handleInput();
        }}
        onKeyDown={handleKeyDown}
        placeholder="Ask about your model or request changes..."
        disabled={disabled}
        rows={1}
        className={cn(
          'flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg',
          'text-sm text-gray-200 placeholder-gray-500',
          'resize-none overflow-hidden',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      />
      <button
        type="submit"
        disabled={disabled || !input.trim()}
        className={cn(
          'px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors',
          'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500',
          (disabled || !input.trim()) && 'opacity-50 cursor-not-allowed'
        )}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      </button>
    </form>
  );
}

/**
 * Suggested prompts for quick actions
 */
function SuggestedPrompts({ onSelect }: { onSelect: (prompt: string) => void }) {
  const hasModel = useIDFStore((state) => state.document !== null);

  const modelPrompts = [
    'Give me a summary of this model',
    'What zones are in my model?',
    'Check for any issues or problems',
    'Show me the HVAC system setup',
  ];

  const noModelPrompts = [
    'What can you help me with?',
    'How do I create a new zone?',
    'Explain EnergyPlus object types',
    'What files do I need to run a simulation?',
  ];

  const prompts = hasModel ? modelPrompts : noModelPrompts;

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500 uppercase tracking-wide">
        {hasModel ? 'Ask about your model' : 'Get started'}
      </div>
      <div className="space-y-1">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => { onSelect(prompt); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-300 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Model status indicator
 */
function ModelStatus() {
  const hasModel = useIDFStore((state) => state.document !== null);
  const objectCount = useIDFStore((state) => state.document?.objects.length ?? 0);
  const hasSchema = useIDDStore((state) => state.isLoaded);

  if (!hasModel) {
    return (
      <div className="px-3 py-2 bg-yellow-900/30 border border-yellow-700/50 rounded-lg text-xs text-yellow-300">
        No model loaded. The AI can answer general EnergyPlus questions.
      </div>
    );
  }

  return (
    <div className="px-3 py-2 bg-green-900/30 border border-green-700/50 rounded-lg text-xs text-green-300 flex items-center gap-2">
      <span className="w-2 h-2 bg-green-400 rounded-full" />
      <span>Model loaded: {objectCount} objects</span>
      {hasSchema && <span className="text-green-400">| Schema ready</span>}
    </div>
  );
}

/**
 * Main MCP AI Chat component
 */
export function McpAiChat() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { isOpen: isWizardOpen } = useWizardState();

  const config = useAIStore((state) => state.config);
  const hasApiKey = useAIStore(selectHasApiKey);
  const isChatOpen = useAIStore((state) => state.isChatOpen);
  const toggleChat = useAIStore((state) => state.toggleChat);
  const chatMessages = useAIStore((state) => state.chatMessages);
  const addChatMessage = useAIStore((state) => state.addChatMessage);
  const updateLastAssistantMessage = useAIStore((state) => state.updateLastAssistantMessage);
  const clearChat = useAIStore((state) => state.clearChat);
  const isChatLoading = useAIStore((state) => state.isChatLoading);
  const setChatLoading = useAIStore((state) => state.setChatLoading);

  // MCP tools
  const { executeTool, hasModel } = useMCPTools();

  // Tool execution tracking
  const [toolExecutions, setToolExecutions] = useState<Map<string, ToolExecution[]>>(new Map());

  // Track feature discovery for onboarding
  useEffect(() => {
    if (isChatOpen) {
      useOnboardingStore.getState().discoverFeature('ai-chat');
    }
  }, [isChatOpen]);

  /**
   * Handle action button clicks from AI responses
   */
  const handleAction = useCallback(
    (action: string) => {
      switch (action) {
        case 'select-weather':
        case 'go-to-weather':
          void navigate('/weather');
          break;
        case 'go-to-simulation':
        case 'run-simulation':
          void navigate('/simulate');
          break;
        case 'go-to-results':
          void navigate('/results');
          break;
        case 'go-to-editor':
          void navigate('/editor');
          break;
        default:
          console.warn(`Unknown action: ${action}`);
      }
    },
    [navigate]
  );

  /**
   * Memoized markdown components with action handler
   */
  const markdownComponents = useMemo(
    () => createMarkdownComponents(handleAction),
    [handleAction]
  );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, toolExecutions]);

  /**
   * Execute MCP tools and return results
   */
  const executeToolsLocally = useCallback(
    (calls: MCPToolCall[]): MCPToolResult[] => {
      return calls.map((call) => executeTool(call));
    },
    [executeTool]
  );

  /**
   * Call Claude API directly with streaming support
   * Text is streamed to the UI as it's generated
   */
  const callClaudeDirectlyStreaming = useCallback(
    async (
      messages: Array<{ role: string; content: unknown }>,
      apiKey: string,
      onTextChunk: (text: string) => void
    ): Promise<{
      text: string;
      toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>;
      stopReason: string;
      assistantContent: unknown;
    }> => {
      // Sanitize messages to ensure tool_use blocks have corresponding tool_results
      const sanitizedMessages = sanitizeMessagesForToolUse(messages);

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 4096,
          system: MCP_SYSTEM_PROMPT,
          messages: sanitizedMessages,
          tools: MCP_TOOLS,
          stream: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: { message?: string } };
        throw new Error(errorData.error?.message || `API error: ${String(response.status)}`);
      }

      if (!response.body) {
        throw new Error('No response body for streaming');
      }

      // Parse SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let fullText = '';
      let stopReason = 'end_turn';
      const contentBlocks: Array<{ type: string; text?: string; id?: string; name?: string; input?: Record<string, unknown> }> = [];
      let currentBlockIndex = -1;
      let currentToolInput = '';

      let buffer = '';

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data) as {
              type: string;
              index?: number;
              content_block?: { type: string; id?: string; name?: string; text?: string };
              delta?: { type: string; text?: string; partial_json?: string; stop_reason?: string };
            };

            switch (event.type) {
              case 'content_block_start':
                currentBlockIndex = event.index ?? -1;
                if (event.content_block) {
                  if (event.content_block.type === 'text') {
                    contentBlocks[currentBlockIndex] = { type: 'text', text: '' };
                  } else if (event.content_block.type === 'tool_use') {
                    contentBlocks[currentBlockIndex] = {
                      type: 'tool_use',
                      id: event.content_block.id ?? '',
                      name: event.content_block.name ?? '',
                      input: {},
                    };
                    currentToolInput = '';
                  }
                }
                break;

              case 'content_block_delta': {
                const currentBlock = contentBlocks[currentBlockIndex];
                if (event.delta?.type === 'text_delta' && event.delta.text) {
                  fullText += event.delta.text;
                  if (currentBlock?.type === 'text') {
                    currentBlock.text = (currentBlock.text || '') + event.delta.text;
                  }
                  onTextChunk(fullText);
                } else if (event.delta?.type === 'input_json_delta' && event.delta.partial_json) {
                  currentToolInput += event.delta.partial_json;
                }
                break;
              }

              case 'content_block_stop': {
                const blockToUpdate = contentBlocks[currentBlockIndex];
                if (blockToUpdate?.type === 'tool_use' && currentToolInput) {
                  try {
                    blockToUpdate.input = JSON.parse(currentToolInput) as Record<string, unknown>;
                  } catch {
                    // Keep empty object if JSON parsing fails
                  }
                }
                break;
              }

              case 'message_delta':
                if (event.delta?.stop_reason) {
                  stopReason = event.delta.stop_reason;
                }
                break;
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }

      const textBlocks = contentBlocks.filter((b) => b.type === 'text');
      const toolUseBlocks = contentBlocks.filter((b) => b.type === 'tool_use');

      const result: {
        text: string;
        toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>;
        stopReason: string;
        assistantContent: unknown;
      } = {
        text: textBlocks.map((b) => b.text || '').join('\n'),
        stopReason,
        assistantContent: contentBlocks,
      };

      if (toolUseBlocks.length > 0) {
        result.toolCalls = toolUseBlocks.map((b) => ({
          id: b.id || '',
          name: b.name || '',
          arguments: b.input || {},
        }));
      }

      return result;
    },
    []
  );

  /**
   * Call server proxy endpoint with streaming support
   * Parses SSE stream and calls onTextChunk for each text delta
   */
  const callServerProxyStreaming = useCallback(
    async (
      conversationHistory: Array<{ role: string; content: unknown }>,
      message: string | null,
      toolResults: Array<{ toolUseId: string; content: unknown; isError?: boolean }> | null,
      onTextChunk: (text: string) => void
    ): Promise<{
      text: string;
      toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>;
      stopReason: string;
      assistantContent: unknown;
    }> => {
      const requestBody: Record<string, unknown> = { conversationHistory, stream: true };

      if (toolResults) {
        requestBody['toolResults'] = toolResults;
      } else if (message) {
        requestBody['message'] = message;
      }

      const response = await fetch('/api/ai/mcp-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as {
          error?: string;
          message?: string;
          retryAfterSeconds?: number;
          details?: string;
        };

        if (response.status === 429 || errorData.error === 'rate_limit') {
          throw new RateLimitError(
            errorData.message || 'Rate limit exceeded',
            errorData.retryAfterSeconds ?? null
          );
        }

        throw new Error(errorData.message || `Server error: ${JSON.stringify(errorData)}`);
      }

      if (!response.body) {
        throw new Error('No response body for streaming');
      }

      // Parse SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let fullText = '';
      let stopReason = 'end_turn';
      const contentBlocks: Array<{ type: string; text?: string; id?: string; name?: string; input?: Record<string, unknown> }> = [];
      let currentBlockIndex = -1;
      let currentToolInput = '';
      let buffer = '';

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data) as {
              type: string;
              index?: number;
              content_block?: { type: string; id?: string; name?: string; text?: string };
              delta?: { type: string; text?: string; partial_json?: string; stop_reason?: string };
            };

            switch (event.type) {
              case 'content_block_start':
                currentBlockIndex = event.index ?? -1;
                if (event.content_block) {
                  if (event.content_block.type === 'text') {
                    contentBlocks[currentBlockIndex] = { type: 'text', text: '' };
                  } else if (event.content_block.type === 'tool_use') {
                    contentBlocks[currentBlockIndex] = {
                      type: 'tool_use',
                      id: event.content_block.id ?? '',
                      name: event.content_block.name ?? '',
                      input: {},
                    };
                    currentToolInput = '';
                  }
                }
                break;

              case 'content_block_delta': {
                const currentBlock = contentBlocks[currentBlockIndex];
                if (event.delta?.type === 'text_delta' && event.delta.text) {
                  fullText += event.delta.text;
                  if (currentBlock?.type === 'text') {
                    currentBlock.text = (currentBlock.text || '') + event.delta.text;
                  }
                  onTextChunk(fullText);
                } else if (event.delta?.type === 'input_json_delta' && event.delta.partial_json) {
                  currentToolInput += event.delta.partial_json;
                }
                break;
              }

              case 'content_block_stop': {
                const blockToUpdate = contentBlocks[currentBlockIndex];
                if (blockToUpdate?.type === 'tool_use' && currentToolInput) {
                  try {
                    blockToUpdate.input = JSON.parse(currentToolInput) as Record<string, unknown>;
                  } catch {
                    // Keep empty object if JSON parsing fails
                  }
                }
                break;
              }

              case 'message_delta':
                if (event.delta?.stop_reason) {
                  stopReason = event.delta.stop_reason;
                }
                break;
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }

      const textBlocks = contentBlocks.filter((b) => b.type === 'text');
      const toolUseBlocks = contentBlocks.filter((b) => b.type === 'tool_use');

      const result: {
        text: string;
        toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>;
        stopReason: string;
        assistantContent: unknown;
      } = {
        text: textBlocks.map((b) => b.text || '').join('\n'),
        stopReason,
        assistantContent: contentBlocks,
      };

      if (toolUseBlocks.length > 0) {
        result.toolCalls = toolUseBlocks.map((b) => ({
          id: b.id || '',
          name: b.name || '',
          arguments: b.input || {},
        }));
      }

      return result;
    },
    []
  );

  /**
   * Send message with MCP tool support
   */
  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!hasApiKey) return;

      // Add user message to UI
      addChatMessage({ role: 'user', content: message });

      // Add placeholder assistant message
      addChatMessage({ role: 'assistant', content: '', isStreaming: true });

      setChatLoading(true);

      // Build conversation history
      const conversationHistory: Array<{ role: string; content: unknown }> = chatMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Determine API mode
      const useDirectApi = !config.useServerProxy && config.apiKey;

      try {
        let currentMessage: string | null = message;
        let toolResults: Array<{ toolUseId: string; content: unknown; isError?: boolean }> | null = null;
        let iterations = 0;
        const maxIterations = 10;
        let accumulatedText = '';
        const messageId = crypto.randomUUID();
        const currentToolExecutions: ToolExecution[] = [];

        // Build messages for direct API
        const apiMessages: Array<{ role: string; content: unknown }> = [...conversationHistory];

        while (iterations < maxIterations) {
          iterations++;

          let data: {
            text: string;
            toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>;
            stopReason: string;
            assistantContent: unknown;
          };

          if (useDirectApi && config.apiKey) {
            // Direct API call with streaming
            if (toolResults) {
              // Add tool results to messages
              apiMessages.push({
                role: 'user',
                content: toolResults.map((tr) => ({
                  type: 'tool_result',
                  tool_use_id: tr.toolUseId,
                  content: JSON.stringify(tr.content),
                  ...(tr.isError ? { is_error: true } : {}),
                })),
              });
            } else if (currentMessage) {
              // Add user message
              apiMessages.push({ role: 'user', content: currentMessage });
            }

            // Stream text chunks to UI as they arrive
            data = await callClaudeDirectlyStreaming(
              apiMessages,
              config.apiKey,
              (streamedText) => {
                const newAccumulated = accumulatedText
                  ? accumulatedText + '\n' + streamedText
                  : streamedText;
                updateLastAssistantMessage(newAccumulated, true);
              }
            );
          } else {
            // Server proxy call with streaming
            data = await callServerProxyStreaming(
              conversationHistory,
              currentMessage,
              toolResults,
              (streamedText) => {
                const newAccumulated = accumulatedText
                  ? accumulatedText + '\n' + streamedText
                  : streamedText;
                updateLastAssistantMessage(newAccumulated, true);
              }
            );
          }

          // Accumulate the final text
          if (data.text) {
            accumulatedText += (accumulatedText ? '\n' : '') + data.text;
          }

          // Check if we need to execute tools
          if (data.stopReason === 'tool_use' && data.toolCalls && data.toolCalls.length > 0) {
            // Update conversation history for direct API
            if (useDirectApi) {
              apiMessages.push({
                role: 'assistant',
                content: data.assistantContent,
              });
            } else {
              // Update conversation ref for server proxy
              conversationHistory.push({
                role: 'assistant',
                content: data.assistantContent,
              });
            }

            // Track tool executions
            const newExecutions: ToolExecution[] = data.toolCalls.map((tc) => ({
              id: tc.id,
              name: tc.name,
              status: 'running' as const,
            }));
            currentToolExecutions.push(...newExecutions);
            setToolExecutions((prev) => new Map(prev).set(messageId, [...currentToolExecutions]));

            // Execute tools locally
            const calls: MCPToolCall[] = data.toolCalls.map((tc) => ({
              name: tc.name,
              arguments: tc.arguments,
            }));

            const results = executeToolsLocally(calls);

            // Update execution status
            for (let i = 0; i < newExecutions.length; i++) {
              const exec = newExecutions[i];
              const result = results[i];
              if (exec && result) {
                exec.status = result.success ? 'completed' : 'error';
                exec.result = result;
              }
            }
            setToolExecutions((prev) => new Map(prev).set(messageId, [...currentToolExecutions]));

            // Prepare tool results for next iteration
            toolResults = data.toolCalls.map((tc, index) => {
              const result: { toolUseId: string; content: unknown; isError?: boolean } = {
                toolUseId: tc.id,
                content: results[index]?.content || { error: results[index]?.error },
              };
              if (results[index]?.isError) {
                result.isError = true;
              }
              return result;
            });

            currentMessage = null;
          } else {
            // No more tool calls, we're done
            break;
          }
        }

        // Finalize the message
        updateLastAssistantMessage(accumulatedText || 'I apologize, but I was unable to generate a response.', false);

        // Store final tool executions
        if (currentToolExecutions.length > 0) {
          setToolExecutions((prev) => new Map(prev).set(messageId, currentToolExecutions));
        }
      } catch (error) {
        // Use user-friendly formatting for rate limit errors
        const errorMessage = formatRateLimitErrorForUser(error);
        updateLastAssistantMessage(
          `Sorry, I encountered an error: ${errorMessage}`,
          false
        );
      } finally {
        setChatLoading(false);
      }
    },
    [
      hasApiKey,
      config,
      chatMessages,
      addChatMessage,
      updateLastAssistantMessage,
      setChatLoading,
      executeToolsLocally,
      callClaudeDirectlyStreaming,
      callServerProxyStreaming,
    ]
  );

  // Floating button when chat is closed
  if (!isChatOpen) {
    if (isMobile && isWizardOpen) {
      return null;
    }
    return (
      <button
        onClick={toggleChat}
        className={cn(
          'fixed right-4 z-50',
          isMobile ? 'bottom-20' : 'bottom-6',
          'w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg',
          'flex items-center justify-center',
          'hover:bg-blue-700 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900',
          isMobile && 'mb-[env(safe-area-inset-bottom)]'
        )}
        data-onboarding="ai-chat-button"
        title="Open AI Assistant"
        aria-label="Open AI Assistant"
      >
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        {/* MCP indicator */}
        {hasModel && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900" />
        )}
      </button>
    );
  }

  // Chat panel
  return (
    <div
      className={cn(
        'fixed z-50',
        isMobile
          ? 'inset-x-2 bottom-20 top-auto h-[70vh] mb-[env(safe-area-inset-bottom)]'
          : 'bottom-6 right-6 w-[420px] h-[650px] max-h-[85vh]',
        'bg-gray-800 rounded-lg shadow-2xl border border-gray-700',
        'flex flex-col overflow-hidden'
      )}
      role="dialog"
      aria-label="AI Chat Assistant"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center relative">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {hasModel && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-800" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-white">AI Assistant</h3>
            <p className="text-xs text-gray-400">
              {hasModel ? 'Model connected' : 'EnergyPlus expert'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearChat}
            className="p-2 text-gray-400 hover:text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
            title="Clear chat"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button
            onClick={toggleChat}
            className="p-2 text-gray-400 hover:text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
            title="Close chat"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Model Status */}
      <div className="px-4 py-2 border-b border-gray-700/50">
        <ModelStatus />
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!hasApiKey ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-200 mb-2">
              AI Assistant Unavailable
            </h4>
            <p className="text-sm text-gray-400 max-w-xs">
              Configure your API key in settings or enable server proxy to use the AI assistant.
            </p>
          </div>
        ) : chatMessages.length === 0 ? (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-200 mb-2">
                How can I help?
              </h4>
              <p className="text-sm text-gray-400">
                {hasModel
                  ? 'I can read, analyze, and modify your EnergyPlus model.'
                  : 'Ask me anything about EnergyPlus modeling.'}
              </p>
            </div>
            <SuggestedPrompts onSelect={handleSendMessage} />
          </div>
        ) : (
          <>
            {chatMessages.map((message) => {
              const executions = message.role === 'assistant' ? toolExecutions.get(message.id) : undefined;

              // Show thinking indicator for assistant messages that are streaming with no content yet
              const isThinking = message.role === 'assistant'
                && message.isStreaming
                && !message.content.trim();

              if (isThinking) {
                return <ThinkingIndicator key={message.id} />;
              }

              return (
                <ChatMessageItem
                  key={message.id}
                  message={message}
                  markdownComponents={markdownComponents}
                  {...(executions ? { toolExecutions: executions } : {})}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-700 bg-gray-800">
        <ChatInput onSend={handleSendMessage} disabled={!hasApiKey || isChatLoading} />
        {hasModel && (
          <p className="text-xs text-gray-500 mt-2 text-center">
            The AI can read and modify your model
          </p>
        )}
      </div>
    </div>
  );
}

export default McpAiChat;
